import React, { useEffect, useRef, useState } from "react";
import { track } from "../lib/mixpanel";
import styles from "../styles/consultsheet.module.css";

/**
 * 휴대폰 인증 공용 모듈 — consultsheet.tsx의 usePhoneVerify/VerifyBlock을 추출.
 * 전송 함수(requestCert/checkCert)를 주입식으로 받아 상담신청(suneungsunbae /api/booking/cert)과
 * 리포트 링크 재인증(peetsunbae /dashboard/reportlink/*) 양쪽에서 재사용한다.
 */

export type CertRequestResult = { ok: boolean; message?: string };
export type CertCheckResult = { ok: boolean; t?: string; message?: string };

export type PhoneVerifyTransport = {
    requestCert: (phoneDigits: string) => Promise<CertRequestResult>;
    checkCert: (phoneDigits: string, certNumber: string) => Promise<CertCheckResult>;
};

// 전화번호 화면 표시용 하이픈 포맷 — 상태·서버 전송값은 숫자만 유지한다.
export const formatPhone = (d: string) => {
    if (d.length < 4) return d;
    if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

export function usePhoneVerify(getFormType: () => string, transport: PhoneVerifyTransport, onVerified?: (t?: string) => void) {
    const [phone, setPhone] = useState("010");
    const [cert, setCert] = useState("");
    const [status, setStatus] = useState<null | "success" | "fail">(null);
    const [clicked, setClicked] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [phoneError, setPhoneError] = useState(false);
    // 발송 단계 오류 문구 (예: 등록된 번호와 불일치) — 있으면 VerifyBlock 아래에 표시
    const [requestError, setRequestError] = useState("");
    const timer = useRef<any>(null);

    useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

    const onPhone = (val: string) => {
        if (status === "success") return;
        setPhone(val.replace(/[^\d]+/g, "").slice(0, 11));
        setPhoneError(false);
        setRequestError("");
    };

    // 실제 발송 여부를 반환 — 발송됐을 때만 호출부에서 인증번호 칸으로 포커스를 옮긴다.
    const request = () => {
        const d = phone.replace(/[^\d]+/g, "");
        if (d.length < 10) { setPhoneError(true); return false; }
        if (cooldown > 0) return false;
        setPhoneError(false);
        setRequestError("");
        track("Phone Verification Requested", { form_type: getFormType() });
        setClicked(true);
        setStatus(null);
        transport.requestCert(d).then((res) => {
            if (!res.ok) {
                // 발송 실패(번호 불일치 등) — 인증번호 칸 닫고 재시도 허용
                setRequestError(res.message || "인증번호 발송에 실패했습니다.");
                setClicked(false);
                setCooldown(0);
                if (timer.current) clearInterval(timer.current);
            }
        }).catch(() => {});
        setCooldown(60);
        if (timer.current) clearInterval(timer.current);
        timer.current = setInterval(() => {
            setCooldown((c) => { if (c <= 1) { clearInterval(timer.current); return 0; } return c - 1; });
        }, 1000);
        return true;
    };

    const onCert = (val: string) => {
        const d = val.replace(/[^\d]+/g, "").slice(0, 6);
        setCert(d);
        if (d.length !== 6) { setStatus(null); return; }
        transport.checkCert(phone.replace(/[^\d]+/g, ""), d).then((res) => {
            if (res.ok) {
                setStatus("success");
                track("Phone Verification Succeeded", { form_type: getFormType() });
                if (onVerified) onVerified(res.t);
            } else {
                setStatus("fail");
                track("Phone Verification Failed", { form_type: getFormType() });
            }
        }).catch(() => setStatus("fail"));
    };

    const reset = () => {
        setPhone("010"); setCert(""); setStatus(null); setClicked(false); setCooldown(0); setPhoneError(false); setRequestError("");
        if (timer.current) clearInterval(timer.current);
    };

    return { phone, onPhone, request, cert, onCert, status, clicked, cooldown, phoneError, requestError, reset, verified: status === "success" };
}

export type Verify = ReturnType<typeof usePhoneVerify>;

export const VerifyBlock: React.FC<{ v: Verify; phoneErr?: boolean; certErr?: boolean }> = ({ v, phoneErr, certErr }) => {
    const certRef = useRef<HTMLInputElement>(null);
    const send = () => {
        // 발송 성공 시 인증번호 입력칸으로 포커스 이동 — 칸이 이 시점에 처음 마운트되므로 렌더 반영 후에 실행.
        if (v.request()) setTimeout(() => certRef.current?.focus(), 0);
    };
    return (
        <>
            <div className={styles.verify}>
                <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={13}
                    placeholder="010-0000-0000"
                    value={formatPhone(v.phone)}
                    onChange={(e) => v.onPhone(e.target.value)}
                    className={(v.phoneError || phoneErr) && !v.verified ? styles.isError : ""}
                />
                <button type="button" className={styles.verifySend} disabled={v.cooldown > 0} onClick={send}>
                    {v.cooldown > 0 ? `재전송 (${v.cooldown}초)` : v.clicked ? "재전송" : "인증번호 받기"}
                </button>
            </div>
            {v.requestError && (
                <div className={styles.verifyRequestError}>{v.requestError}</div>
            )}
            {v.clicked && (
                <div className={styles.verifyCode}>
                    <input
                        ref={certRef}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="인증번호"
                        autoComplete="one-time-code"
                        value={v.cert}
                        onChange={(e) => v.onCert(e.target.value)}
                        className={certErr && !v.verified ? styles.isError : ""}
                    />
                    <span className={`${styles.verifyStatus} ${v.status === "fail" ? styles.verifyStatusFail : ""}`}>
                        {v.status === "success" ? "✓ 인증완료" : v.status === "fail" ? "인증 실패" : ""}
                    </span>
                </div>
            )}
        </>
    );
};
